import dayjs from 'dayjs';

import { convertToLocalTime } from '../../../lib/convert';
import type { DurationRange, RecordFilters } from '../../shared';

export const mapRecordToRow = (record: any, index: number) => {
    const timeZone = dayjs().format('UTCZ');
    const durationTime = (record.number_of_records * record.interval) / 60;
    const durationMin = durationTime % 60;
    const durationHour = Math.floor(durationTime / 60) % 24;
    const durationDay = Math.floor(durationTime / 60 / 24);
    const duration = durationTime ? `${durationDay}D ${durationHour}H ${durationMin}M` : '';

    // Copy before converting: the alert times used to be rewritten in place on the
    // API object, which double-converts as soon as the same object is mapped twice.
    const threshold = record.threshold ? { ...record.threshold } : record.threshold;
    if (threshold?.alert_start_time) {
        threshold.alert_start_time = convertToLocalTime(threshold.alert_start_time);
    }
    if (threshold?.alert_end_time) {
        threshold.alert_end_time = convertToLocalTime(threshold.alert_end_time);
    }

    const labelIds = Array.isArray(record.label_ids)
        ? record.label_ids
        : Array.isArray(record.labelIds)
          ? record.labelIds
          : [];

    return {
        num: index + 1,
        id: `${record.device_id}-${record.index}`,
        createTime: convertToLocalTime(record.create_time),
        startTime: convertToLocalTime(record.start_time),
        endTime: convertToLocalTime(record.end_time),
        length: record.number_of_records,
        duration,
        interval: record.interval,
        timeZone,
        company: record.meta_data.shipper_name,
        status: record.is_normal,
        trackNum: record.meta_data.shipment_number,
        note: record.meta_data.note,
        deviceId: record.device_id,
        index: record.index,
        lowerLimit: threshold.lower_limit,
        upperLimit: threshold.upper_limit,
        timeLimit: threshold.time_limit,
        totalMinutes: record.total_minute,
        thresholdType: threshold.type,
        metaData: record.meta_data || {},
        threshold: threshold || {},
        notifyEmails: record.notify_emails || [],
        labelIds,
    };
};

export type PendingRow = ReturnType<typeof mapRecordToRow>;

// Newest first; records with no create_time sink to the bottom.
export const toPendingRows = (records: any[]): PendingRow[] =>
    [...records]
        .sort((a: any, b: any) => {
            if (!a.create_time) return 1;
            if (!b.create_time) return -1;
            return dayjs(b.create_time).unix() - dayjs(a.create_time).unix();
        })
        .map(mapRecordToRow);

const matchesStatus = (row: any, status: string) => {
    if (status === 'any') return true;
    const isNormal = row.status === true || row.status === 1 || row.status === 'normal';
    const isAbnormal = row.status === false || row.status === 0 || row.status === 'abnormal';
    if (status === 'pass') return isNormal;
    if (status === 'fail') return isAbnormal;
    return true;
};

const matchesDeviceType = (row: any, deviceType: string) => {
    if (deviceType === 'all') return true;
    const model = (row.modelInfo?.model || '').toString().toLowerCase();
    const isLite = model.includes('lite');
    const isReuse = model.includes('reuse') || model.includes('re-use') || model.includes('re use');
    const isCotag = model.includes('cotag');
    if (deviceType === 'cotag') return isCotag && !isLite && !isReuse;
    if (deviceType === 'cotagLite') return isLite;
    if (deviceType === 'cotagReuse') return isReuse;
    return true;
};

const matchesDuration = (row: any, [minDays, maxDays]: DurationRange) => {
    const totalMinutes = row.totalMinutes ?? (row.length && row.interval ? (row.length * row.interval) / 60 : null);
    // Rows with no duration only survive a filter that starts at zero.
    if (totalMinutes === null) return minDays <= 0;
    const durationDays = totalMinutes / (60 * 24);
    const maxBound = Number.isFinite(maxDays) ? maxDays : Number.POSITIVE_INFINITY;
    return durationDays >= minDays && durationDays <= maxBound;
};

const matchesLabels = (row: any, labels: string[]) =>
    labels.length === 0 || (Array.isArray(row.labelIds) && row.labelIds.some((id: any) => labels.includes(id)));

export const filterPendingRows = (rows: PendingRow[], filters: RecordFilters) =>
    rows.filter(
        (row: any) =>
            (!filters.search || (row.deviceId ? row.deviceId.includes(filters.search) : false)) &&
            matchesStatus(row, filters.status) &&
            matchesDeviceType(row, filters.deviceType) &&
            matchesDuration(row, filters.durationRange) &&
            matchesLabels(row, filters.labels)
    );
